import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import emailjs from '@emailjs/browser';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import HeadJsonLd from '../components/HeadJsonLd';
import { articlePath } from '../lib/articles';

const BackToTopButton = () => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const { language } = useLanguage();

  React.useEffect(() => {
    const handleScroll = () => {
      if (!btnRef.current) return;
      if (window.scrollY > 600) {
        btnRef.current.classList.remove('opacity-0', 'pointer-events-none');
        btnRef.current.classList.add('opacity-100');
      } else {
        btnRef.current.classList.add('opacity-0', 'pointer-events-none');
        btnRef.current.classList.remove('opacity-100');
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <button
      ref={btnRef}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-6 left-6 z-50 w-12 h-12 bg-navy/80 hover:bg-navy rounded-full flex items-center justify-center shadow-lg transition-all opacity-0 pointer-events-none"
      aria-label={language === 'bg' ? 'Нагоре' : 'Back to top'}
    >
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    </button>
  );
};

const AwardsGrid: React.FC<{ awards: import('../contexts/LanguageContext').Award[] }> = ({ awards }) => {
  const { refetch } = useLanguage();
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(prev => Math.max(0.5, Math.min(3, prev + delta)));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      dragStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPan({
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const closeLightbox = () => {
    setLightboxUrl(null);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  return (
    <>
      {awards.length === 0 && (
        <div className="text-center py-8">
          <button onClick={refetch} className="px-6 py-2 bg-navy text-white rounded-lg text-sm hover:bg-navy/80 transition-colors">
            ↺ Reload Awards
          </button>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {awards.map((award) => (
          <div key={award.id} className="flex flex-col overflow-hidden rounded-xl border border-[#e2dccf] bg-white">
            {award.image_url && (
              <div className="cursor-zoom-in overflow-hidden border-b border-[#e2dccf] bg-[#f8f6f1] p-2" onClick={() => { if (award.image_url) { setLightboxUrl(award.image_url); setZoom(1); setPan({ x: 0, y: 0 }); } }}>
                <img src={award.image_url} alt={award.title} className="max-h-[26rem] w-full object-contain" />
              </div>
            )}
            <div className="px-4 py-3 flex flex-col flex-grow">
              {award.year && <span className="text-[#8a651a] text-sm font-medium mb-1">{award.year}</span>}
              <h3 className="text-base font-serif text-navy mb-1">{award.title}</h3>
              {award.description && <p className="text-slate text-sm leading-snug">{award.description}</p>}
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox - rendered via portal to escape parent CSS */}
      {lightboxUrl && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2147483647,
            cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
          }}
          onClick={closeLightbox}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div style={{ position: 'absolute', top: 20, left: 20, color: 'white', fontSize: 14, opacity: 0.8 }}>
            Scroll to zoom • Drag to pan • Click background to close
          </div>
          <img
            src={lightboxUrl}
            alt=""
            style={{
              maxWidth: '95%',
              maxHeight: '95%',
              objectFit: 'contain',
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transition: isDragging ? 'none' : 'transform 0.1s ease-out',
              cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
    </>
  );
};

// One-pager homepage with all sections
const OnePagerHome = () => {
  const { language, t, articles, awards, mediaMentions, loading } = useLanguage();
  const heroHighlights = React.useMemo(() => [
    { kind: 'award' as const, id: 'award-jurist-2017', source_url: 'https://www.24chasa.bg/bulgaria/article/6834047' },
    ...mediaMentions.map(mention => ({ ...mention, kind: 'media' as const })),
  ], [mediaMentions]);
  const mediaBeltRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<string | null>(null);
  const [sendStatusType, setSendStatusType] = useState<'success' | 'error' | null>(null);
  const [messageLength, setMessageLength] = useState(0);
  const formStartTime = useRef<number>(Date.now());
  const [activeSection, setActiveSection] = useState('home');

  React.useEffect(() => {
    document.body.setAttribute('data-lang', language);
    document.documentElement.setAttribute('lang', language === 'bg' ? 'bg' : 'en');
  }, [language]);

  React.useEffect(() => {
    const belt = mediaBeltRef.current;
    if (!belt || heroHighlights.length < 2) return;
    const links = Array.from(belt.querySelectorAll<HTMLAnchorElement>('.media-mention-track > a'));
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const resetTransforms = () => links.forEach(link => { link.style.transform = ''; });
    let frame: number | null = null;
    let lastTime = 0;
    let distance = 0;
    let positions: number[] = [];
    let layoutPositions: number[] = [];
    let circuit = 0;
    let circuitBase = 0;

    const tick = (time: number) => {
      frame = null;
      if (motion.matches) {
        resetTransforms();
        lastTime = 0;
        return;
      }
      if (!Number.isFinite(circuit) || circuit <= 0) return;
      if (lastTime && !document.hidden && !belt.matches(':hover, :focus-within')) {
        distance += Math.min(time - lastTime, 50) * 0.13104;
      }
      lastTime = time;
      links.forEach((link, index) => {
        const position = ((positions[index] + distance - circuitBase) % circuit) + circuitBase;
        link.style.transform = `translate3d(${position - layoutPositions[index]}px, 0, 0)`;
      });
      frame = window.requestAnimationFrame(tick);
    };

    const measure = () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
      lastTime = 0;
      resetTransforms();
      positions = [];
      layoutPositions = [];
      circuit = 0;
      circuitBase = 0;
      if (motion.matches) return;
      const beltLeft = belt.getBoundingClientRect().left;
      layoutPositions = links.map(link => link.getBoundingClientRect().left - beltLeft);
      const span = layoutPositions[heroHighlights.length] - layoutPositions[0];
      circuit = span * (links.length / heroHighlights.length);
      if (!Number.isFinite(span) || span <= 0 || !Number.isFinite(circuit) || circuit <= 0) {
        circuit = 0;
        return;
      }
      positions = layoutPositions.map(position => position - layoutPositions[0] + 16);
      circuitBase = belt.clientWidth - circuit;
      if (!positions.every(Number.isFinite) || !Number.isFinite(circuitBase)) {
        positions = [];
        circuit = 0;
        return;
      }
      frame = window.requestAnimationFrame(tick);
    };

    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(belt);
    motion.addEventListener('change', measure);
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      motion.removeEventListener('change', measure);
    };
  }, [heroHighlights]);

  React.useEffect(() => {
    const sections = ['home', 'about', 'awards', 'practice', 'notable-cases', 'faq', 'blog', 'contact'];
    let frame: number | null = null;

    const updateActiveSection = () => {
      frame = null;
      const threshold = Math.max(80, window.innerHeight * 0.2);
      let nextSection = 'home';
      sections.forEach(id => {
        const section = document.getElementById(id);
        if (section && section.getBoundingClientRect().top <= threshold) nextSection = id;
      });
      setActiveSection(nextSection);
    };

    const scheduleUpdate = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(updateActiveSection);
    };

    scheduleUpdate();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [awards.length, articles.length, mediaMentions.length, loading, language]);

  React.useEffect(() => {
    const animObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px -8% 0px' }
    );
    document.querySelectorAll('.scroll-animate').forEach((el) => animObserver.observe(el));
    return () => animObserver.disconnect();
  }, [awards.length, articles.length, mediaMentions.length]);

  React.useEffect(() => {
    if (loading) return;
    const hash = window.location.hash;
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [loading]);

  const sendEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;

    // Spam protection: minimum fill time (5 seconds)
    const timeElapsed = Date.now() - formStartTime.current;
    if (timeElapsed < 5000) {
      setSendStatusType('error');
      setSendStatus(language === 'bg' ? 'Моля, изчакайте малко преди изпращане.' : 'Please wait a moment before submitting.');
      setTimeout(() => setSendStatus(null), 5000);
      return;
    }

    // Spam protection: honeypot check
    const honeypot = formRef.current.querySelector('[name="website"]') as HTMLInputElement;
    if (honeypot && honeypot.value) {
      // Bot detected - silently reject
      setSendStatusType('success');
      setSendStatus(language === 'bg' ? 'Съобщението е изпратено!' : 'Message sent!');
      formRef.current.reset();
      setMessageLength(0);
      setTimeout(() => setSendStatus(null), 5000);
      return;
    }

    // Set timestamp for EmailJS {{time}} variable
    const timeField = formRef.current.querySelector('[name="time"]') as HTMLInputElement;
    if (timeField) {
      const now = new Date();
      const formatted = now.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      timeField.value = formatted;
    }

    setIsSending(true);
    setSendStatus(null);

    emailjs
      .sendForm(
        'service_jn36meo',
        'template_vbaqzwn',
        formRef.current,
        'oR72Jz7bjo1f1J7Kx'
      )
      .then(
        (result: { text: string }) => {
          console.log('Email sent:', result.text);
          setSendStatusType('success');
          setSendStatus(language === 'bg' ? 'Съобщението е изпратено успешно!' : 'Message sent successfully!');
          if (formRef.current) formRef.current.reset();
          setMessageLength(0);
        },
        (error: { text: string }) => {
          console.error('Email error:', error.text);
          setSendStatusType('error');
          setSendStatus(language === 'bg' ? 'Грешка при изпращане. Опитайте отново.' : 'Error sending. Please try again.');
        }
      )
      .finally(() => {
        setIsSending(false);
        setTimeout(() => setSendStatus(null), 5000);
      });
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{language === 'bg' ? 'Адвокатска кантора Петър Николов | Варна и София | Гражданско право' : 'Petar Nikolov Law Firm | Varna & Sofia | Civil Law'}</title>
        <meta name="description" content={language === 'bg' ? 'Адвокатска кантора Петър Николов - семейно и вещно право, имотни спорове, родителски права и защита от кредитори. Варна, София и цяла България.' : 'Petar Nikolov Law Firm: family and property law, real estate disputes, parental rights and debtor protection in Varna, Sofia and throughout Bulgaria.'} />
        <link rel="canonical" href="https://advokatnikolov.com/" />
        <link rel="alternate" hrefLang="bg" href="https://advokatnikolov.com/" />
        <link rel="alternate" hrefLang="en" href="https://advokatnikolov.com/" />
      </Helmet>
      <NavBar isHomepage activeSection={activeSection} onSectionClick={setActiveSection} />

      {/* Hero Section */}
      <section id="home" className="pt-20 relative min-h-screen flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/95 to-accent"></div>
        <div className="absolute inset-0 opacity-10"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-center">
            <div className="animate-fade-in w-full text-center">
              <div className="inline-flex items-center space-x-2 bg-gold/10 border border-gold/30 rounded-full px-4 py-2 mt-4 mb-6 lg:mb-10">
                <span className="w-2 h-2 bg-gold rounded-full animate-pulse"></span>
                <span className="text-gold font-medium text-sm">{t('hero.established')}</span>
              </div>
              
              <h1 className="section-title text-white mb-6 lg:mb-10">
                {t('hero.title')}
              </h1>
              
              <h2 className="text-3xl lg:text-4xl font-serif text-gold mb-6 lg:mb-12 font-light">
                <span className="lg:hidden">
                  {language === 'bg' ? 'Семейно право • Имотни спорове • Защита срещу кредитори' : 'Family Law • Property Disputes • Creditor Protection'}
                </span>
                <span className="hidden lg:inline">
                  {t('hero.subtitle')}
                </span>
              </h2>

              <p className="text-xl text-gray-300 mb-8 lg:mb-12 leading-relaxed px-4 lg:px-16">
                {t('hero.description')}
              </p>

              {heroHighlights.length > 0 && <div className="mb-6 lg:mb-8">
                <p className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-gold">{t('media.heroBadge')}</p>
                <div className={`media-mention-belt ${heroHighlights.length === 1 ? 'media-mention-static' : ''}`}>
                  <div ref={mediaBeltRef} className="media-mention-viewport">
                    <div className="media-mention-track">
                      {(heroHighlights.length > 1 ? [0, 1] : [0]).flatMap(copy => heroHighlights.map(highlight => <a key={`${copy}-${highlight.id}`} href={highlight.source_url} target="_blank" rel="noopener noreferrer" aria-label={highlight.kind === 'award' ? `${t('awards.prizeTitle')}, ${t('awards.awarded')} · ${t('awards.source')}: ${language === 'bg' ? '24 часа' : '24 Chasa'}` : undefined} aria-hidden={copy > 0 || undefined} tabIndex={copy > 0 ? -1 : undefined} className={`group flex items-center gap-3 rounded-2xl border border-white/15 bg-white/10 p-3 text-left shadow-lg backdrop-blur-sm transition hover:border-gold/60 hover:bg-white/15 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 sm:gap-4${copy > 0 ? ' media-mention-dup' : ''}`}>
                        {highlight.kind === 'award' ? <>
                          <span className="flex shrink-0 items-center rounded-xl bg-white px-2 py-2 sm:px-3">
                            <img src="/assets/images/24-chasa-logo.svg" alt={language === 'bg' ? '24 часа' : '24 Chasa'} className="h-6 w-[84px] object-contain sm:w-[106px]" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold leading-snug text-white group-hover:text-gold sm:text-base">{t('awards.prizeTitle')}</span>
                            <span className="mt-1 block text-xs leading-snug text-gray-300">{t('awards.awarded')} · {t('awards.source')}: {language === 'bg' ? '24 часа' : '24 Chasa'}</span>
                          </span>
                        </> : <>
                          <span className="flex shrink-0 items-center rounded-xl bg-white px-2 py-2 sm:px-3">
                            <img src={highlight.logo_path} alt={language === 'bg' ? highlight.publisher_bg : highlight.publisher_en} className="h-6 w-[84px] object-contain sm:w-[106px]" />
                          </span>
                          <span className="min-w-0 flex-1 text-sm font-semibold leading-snug text-white group-hover:text-gold sm:text-base">{language === 'bg' ? highlight.title_bg : highlight.title_en}</span>
                        </>}
                      </a>))}
                    </div>
                  </div>
                </div>
              </div>}
              
              <div className="flex flex-col sm:flex-row gap-8 justify-center mb-6 lg:mb-8">
                <a href="#contact" className="btn-primary">{t('hero.freeConsultation')}</a>
                <a href="#practice" className="btn-secondary">{t('hero.ourServices')}</a>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" aria-labelledby="about-heading" className="py-10 lg:py-16 bg-[#f8f6f1] scroll-animate">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          
          <div className="overflow-hidden rounded-[1.75rem] border border-white/10 border-t-2 border-t-gold bg-navy px-6 py-8 text-white sm:px-10 sm:py-10 lg:px-12 lg:py-12">
            <div className="flex flex-col gap-3 border-b border-white/15 pb-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gold">{t('about.badge')}</p>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-300">{t('about.firmLocation')}</p>
            </div>
            <div className="mt-8 grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10 xl:grid-cols-[minmax(0,1fr)_360px] xl:gap-12">
              <div className="min-w-0">
                <h2 id="about-heading" className="max-w-3xl font-serif text-3xl leading-[1.12] text-white sm:text-4xl xl:text-5xl">{t('about.title')}</h2>
                <p className="mt-5 max-w-2xl text-lg leading-relaxed text-gray-200">{t('about.subtitle')}</p>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-white">{t('about.teamStatement')}</p>
                <p className="mt-3 max-w-2xl text-base leading-relaxed text-gray-300">{t('about.teamDetail')}</p>
              </div>
              <div className="mx-auto w-full max-w-[320px] lg:max-w-none">
                <div className="flex aspect-square w-full items-center justify-center rounded-2xl border border-gold/25 bg-[#f8f6f1] p-3">
                  <img
                    src="/assets/images/nikolov-law-logo-vector-preview.png"
                    alt={t('about.logoAlt')}
                    loading="lazy"
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
            <div className="mt-8 border-t border-white/15 pt-6 lg:mt-10">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="flex items-center gap-3">
                  <span aria-hidden="true" className="h-px w-5 shrink-0 bg-gold/70" />
                  <p className="text-sm font-semibold leading-relaxed text-white">{t('about.teamStrategy')}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span aria-hidden="true" className="h-px w-5 shrink-0 bg-gold/70" />
                  <p className="text-sm font-semibold leading-relaxed text-white">{t('about.teamRepresentation')}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span aria-hidden="true" className="h-px w-5 shrink-0 bg-gold/70" />
                  <p className="text-sm font-semibold leading-relaxed text-white">{t('about.teamCoordination')}</p>
                </div>
              </div>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <a href="#practice" className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-gold px-5 py-3 text-center font-semibold text-navy transition-colors hover:bg-[#cfae45] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/50 motion-reduce:transition-none">{t('about.practiceCta')}</a>
                <a href="#awards" className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-white/35 bg-white/5 px-5 py-3 text-center font-semibold text-white transition-colors hover:border-gold/80 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/50 motion-reduce:transition-none">{t('about.evidenceCta')}</a>
              </div>
            </div>
          </div>

          <div className="mt-10 lg:mt-14">
            <div className="mb-4 border-b border-[#d8d2c5] pb-5">
              <h3 className="max-w-3xl font-serif text-3xl leading-tight text-navy sm:text-4xl">{t('about.practiceIndexTitle')}</h3>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4">
              <a href={language === 'bg' ? '/semeino-pravo-roditelski-prava' : '/family-law-parental-rights'} className="group border-b border-[#d8d2c5] px-4 py-6 transition-colors hover:bg-white/60 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/40 motion-reduce:transition-none sm:px-5 lg:border-b-0 lg:border-r">
                <div className="flex items-center justify-between gap-4"><span aria-hidden="true" className="font-serif text-4xl text-[#8a651a]">01</span><svg className="h-4 w-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14m-6-6 6 6-6 6" /></svg></div>
                <h4 className="mt-5 font-serif text-lg leading-snug text-navy">{t('about.practiceIndexFamily')}</h4>
              </a>
              <a href={language === 'bg' ? '/veshtno-pravo-nedvizhimi-imoti' : '/property-law-real-estate'} className="group border-b border-[#d8d2c5] px-4 py-6 transition-colors hover:bg-white/60 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/40 motion-reduce:transition-none sm:px-5 lg:border-b-0 lg:border-r">
                <div className="flex items-center justify-between gap-4"><span aria-hidden="true" className="font-serif text-4xl text-[#8a651a]">02</span><svg className="h-4 w-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14m-6-6 6 6-6 6" /></svg></div>
                <h4 className="mt-5 font-serif text-lg leading-snug text-navy">{t('about.practiceIndexProperty')}</h4>
              </a>
              <a href={language === 'bg' ? '/zashtita-dlazhnitsi-potrebiteli' : '/debtor-consumer-protection'} className="group border-b border-[#d8d2c5] px-4 py-6 transition-colors hover:bg-white/60 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/40 motion-reduce:transition-none sm:px-5 lg:border-b-0 lg:border-r">
                <div className="flex items-center justify-between gap-4"><span aria-hidden="true" className="font-serif text-4xl text-[#8a651a]">03</span><svg className="h-4 w-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14m-6-6 6 6-6 6" /></svg></div>
                <h4 className="mt-5 font-serif text-lg leading-snug text-navy">{t('about.practiceIndexDebt')}</h4>
              </a>
              <a href={language === 'bg' ? '/administrativno-pravo' : '/administrative-law'} className="group border-b border-[#d8d2c5] px-4 py-6 transition-colors hover:bg-white/60 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/40 motion-reduce:transition-none last:border-b-0 sm:px-5 lg:border-b-0">
                <div className="flex items-center justify-between gap-4"><span aria-hidden="true" className="font-serif text-4xl text-[#8a651a]">04</span><svg className="h-4 w-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14m-6-6 6 6-6 6" /></svg></div>
                <h4 className="mt-5 font-serif text-lg leading-snug text-navy">{t('about.practiceIndexAdmin')}</h4>
              </a>
            </div>
            <div className="flex justify-end border-b border-[#d8d2c5] py-3">
              <a href="#public-causes" className="inline-flex min-h-[44px] items-center gap-2 font-semibold text-navy transition-colors hover:text-gold focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/40 motion-reduce:transition-none">
                <span>{t('about.publicCauses')}</span>
                <svg className="h-4 w-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14m-6-6 6 6-6 6" /></svg>
              </a>
            </div>
          </div>

          <section aria-labelledby="about-evidence-heading" className="mt-10 grid gap-6 border-t border-[#d8d2c5] border-l-2 border-l-gold bg-white/80 p-6 pt-8 lg:mt-14 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-8 sm:p-8">
            <figure className="mx-auto w-full max-w-[240px] self-start sm:max-w-[252px] lg:mx-0 lg:w-[240px]">
              <div className="h-[260px] w-full overflow-hidden rounded-md bg-[#d9d2c5] sm:h-[300px]">
                <img
                  src="/assets/images/petar-nikolov-lawyer.jpg"
                  alt={t('about.portraitAlt')}
                  loading="lazy"
                  className="h-full w-full object-cover object-[50%_10%]"
                  onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
              <figcaption className="mt-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8a651a]">{t('about.founderEyebrow')}</p>
                <p className="mt-1 font-serif text-xl text-navy">{t('about.name')}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate">{t('about.founderIntro')}</p>
              </figcaption>
            </figure>
            <div className="min-w-0">
              <h3 id="about-evidence-heading" className="font-serif text-2xl text-navy">{t('about.evidenceIntro')}</h3>
              <p className="mt-4 leading-relaxed text-slate">{t('about.description1')}</p>
              <p className="mt-4 leading-relaxed text-slate">{t('about.practiceFocus')}</p>
              <p className="mt-4 leading-relaxed text-slate">{t('about.description3')}</p>
              <a href="#notable-cases" className="mt-4 inline-flex min-h-[44px] items-center font-semibold text-[#8a651a] underline decoration-[#8a651a]/40 underline-offset-4 hover:text-navy focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/40 motion-reduce:transition-none">{t('nav.notableCases')}</a>
              <div className="mt-8 border-t border-[#e2dccf] pt-5">
                <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate">{t('about.credentials')}</h4>
                <ul className="mt-4 space-y-3">
                  <li className="flex items-start gap-3"><span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 bg-gold"></span><span className="text-sm leading-relaxed text-slate">{t('about.sofiaUniversity')}</span></li>
                  <li className="flex items-start gap-3"><span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 bg-gold"></span><span className="text-sm leading-relaxed text-slate">{t('about.barAssociation')}</span></li>
                  <li className="flex items-start gap-3"><span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 bg-gold"></span><span className="text-sm leading-relaxed text-slate">{t('about.lawyerOfTheYear')}</span></li>
                </ul>
              </div>
            </div>
          </section>

          {/* Awards Section */}
          <section id="awards" className="py-10 lg:py-14 bg-[#f8f6f1] scroll-animate">
            <div className="mb-8 max-w-4xl">
              <div className="mb-3 flex items-center gap-3">
                <span className="h-px w-10 bg-gold"></span>
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8a651a]">{t('awards.badge')}</span>
              </div>
              <h2 id="awards-heading" className="font-serif text-3xl text-navy sm:text-4xl">{t('awards.title')}</h2>
              <p className="mt-3 max-w-3xl text-base leading-relaxed text-slate">{t('awards.subtitle')}</p>
            </div>
            <div className="grid max-w-5xl gap-5 md:grid-cols-2">
              <article className="flex h-full flex-col border border-[#e2dccf] border-l-2 border-l-gold bg-white p-6 sm:p-8">
                <div className="flex items-center justify-between gap-3">
                  <span className="inline-flex w-fit border border-gold/30 bg-gold/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#8a651a]">{t('awards.awarded')}</span>
                  <span className="text-sm font-semibold text-slate">2017</span>
                </div>
                <h3 className="mt-5 text-2xl font-serif text-navy">{t('awards.prizeTitle')}</h3>
                <p className="mt-3 text-sm font-semibold text-[#8a651a]">{t('awards.prizeOrganiser')}</p>
                <p className="mt-4 flex-grow leading-relaxed text-slate">{t('awards.prizeDescription')}</p>
                <a href="https://www.24chasa.bg/bulgaria/article/6834047" target="_blank" rel="noopener noreferrer" aria-label={`${t('awards.source')}: ${language === 'bg' ? '24 часа' : '24 Chasa'}`} className="mt-6 inline-flex min-h-[44px] items-center font-semibold text-[#8a651a] underline decoration-gold/40 underline-offset-4 hover:text-navy focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/50">{t('awards.source')}</a>
              </article>
              <article className="flex h-full flex-col border border-[#e2dccf] border-l-2 border-l-navy bg-white p-6 sm:p-8">
                <div className="flex items-center justify-between gap-3">
                  <span className="inline-flex w-fit border border-navy/15 bg-navy/5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-navy">{t('awards.nominated')}</span>
                  <span className="text-sm font-semibold text-slate">2021</span>
                </div>
                <h3 className="mt-5 text-2xl font-serif text-navy">{t('awards.nomineeTitle')}</h3>
                <p className="mt-3 text-sm font-semibold text-[#8a651a]">{t('awards.nomineeOrganiser')}</p>
                <p className="mt-4 flex-grow leading-relaxed text-slate">{t('awards.nomineeDescription')}</p>
                <a href="https://iusauthor.com/novini/445-iusauthor-kantora-na-godinata-2021.html" target="_blank" rel="noopener noreferrer" aria-label={`${t('awards.source')}: IUS AUTHOR`} className="mt-6 inline-flex min-h-[44px] items-center font-semibold text-[#8a651a] underline decoration-gold/40 underline-offset-4 hover:text-navy focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/50">{t('awards.source')}: IUS AUTHOR</a>
              </article>
            </div>
            {(awards.filter(a => a.language === language).length > 0 || process.env.NODE_ENV === 'development') && (
              <div className="mt-10 border-t border-[#d8d2c5] pt-8 lg:mt-12">
                <h3 className="mb-5 font-serif text-xl text-navy">{t('awards.documentsTitle')}</h3>
                <AwardsGrid awards={awards.filter(a => a.language === language).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))} />
              </div>
            )}
          </section>

          <div id="public-causes" className="mt-8 scroll-mt-24 border-t border-[#d8d2c5] pt-8 lg:mt-12">
            <h3 className="font-serif text-2xl text-navy mb-3">{t('about.publicCauses')}</h3>
            <p className="max-w-4xl leading-relaxed text-slate">{t('about.description3')}</p>
          </div>

          {mediaMentions.length > 0 && <section id="media" aria-labelledby="media-heading" className="scroll-animate">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-navy via-navy to-accent text-white shadow-2xl">
              <div className="absolute inset-y-0 left-0 w-2 bg-gold" />
              <h2 id="media-heading" className="pt-8 text-center text-3xl font-serif font-bold text-gold md:pt-12 md:text-4xl">{t('media.badge')}</h2>
              <div className="divide-y divide-white/40">
                {mediaMentions.map(mention => <article key={mention.id} className="grid gap-8 p-8 md:p-12 lg:grid-cols-[220px_minmax(0,1fr)] lg:items-center">
                  <div className="flex flex-col items-center justify-center self-center rounded-2xl bg-white px-6 py-8 text-center shadow-xl ring-1 ring-black/5">
                    <img src={mention.logo_path} alt={language === 'bg' ? mention.publisher_bg : mention.publisher_en} className="h-12 w-full max-w-[212px] object-contain" />
                    <div className="mt-6 h-px w-full bg-gray-200" />
                    <span className="mt-4 text-sm font-medium text-slate">{language === 'bg' ? mention.publisher_bg : mention.publisher_en}</span>
                  </div>
                  <div className="self-center">
                    <h3 className="text-3xl font-serif leading-tight text-white md:text-4xl">{language === 'bg' ? mention.title_bg : mention.title_en}</h3>
                    <p className="mt-5 max-w-3xl text-lg leading-relaxed text-gray-200">{language === 'bg' ? mention.summary_bg : mention.summary_en}</p>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:justify-center lg:col-span-2">
                    <a href={articlePath(language === 'bg' ? mention.article_bg_slug : mention.article_en_slug)} className="inline-flex items-center justify-center rounded-lg bg-gold px-6 py-3 font-semibold text-navy transition hover:bg-yellow-500 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/50">{t('media.readArticle')}</a>
                    <a href={mention.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-lg border border-white/30 px-6 py-3 font-semibold text-white transition hover:border-gold hover:text-gold focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/30">{t('media.watchReport')}</a>
                  </div>
                </article>)}
              </div>
            </div>
          </section>}
        </div>
      </section>

      {/* Practice Areas Section */}
      <section id="practice" className="py-12 lg:py-24 bg-white scroll-animate">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-16">
            <div className="inline-flex items-center space-x-2 bg-gold/10 rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-gold rounded-full"></span>
                <span className="text-gold font-medium text-sm">{t('practice.badge')}</span>
              </div>
            <h2 className="section-title text-center">{t('practice.title')}</h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              {t('practice.subtitle')}
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Child Protection & Family Rights */}
            <a href={language === 'bg' ? '/semeino-pravo-roditelski-prava' : '/family-law-parental-rights'} className="group flex flex-col bg-navy rounded-2xl p-8 text-white transition hover:-translate-y-1 hover:shadow-xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/60">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl">👨‍👩‍👧‍👦</span>
                </div>
                <div>
                  <h3 className="text-2xl font-serif text-white mb-1">{t('practice.childProtectionTitle')}</h3>
                  <p className="text-gold font-medium text-sm">{t('practice.childProtection')}</p>
                </div>
              </div>
              
              <p className="text-gray-300 mb-6 leading-relaxed">
                {t('practice.childProtectionDesc')}
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gold rounded-full"></div>
                  <span className="text-gray-300">{t('practice.childReturn')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gold rounded-full"></div>
                  <span className="text-gray-300">{t('practice.parentalRights')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gold rounded-full"></div>
                  <span className="text-gray-300">{t('practice.socialServicesDefense')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gold rounded-full"></div>
                  <span className="text-gray-300">{t('practice.divorce')}</span>
                </div>
              </div>
            </a>
            
            {/* Consumer & Debtor Protection */}
            <a href={language === 'bg' ? '/zashtita-dlazhnitsi-potrebiteli' : '/debtor-consumer-protection'} className="group flex flex-col bg-accent rounded-2xl p-8 text-white transition hover:-translate-y-1 hover:shadow-xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/60">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🛡️</span>
                </div>
                <div>
                  <h3 className="text-2xl font-serif text-white mb-1">{t('practice.debtorProtectionTitle')}</h3>
                  <p className="text-gold font-medium text-sm">{t('practice.debtorProtection')}</p>
                </div>
              </div>
              
              <p className="text-gray-300 mb-6 leading-relaxed">
                {t('practice.debtorProtectionDesc')}
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gold rounded-full"></div>
                  <span className="text-gray-300">{t('practice.bailiffDefense')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gold rounded-full"></div>
                  <span className="text-gray-300">{t('practice.bankDisputes')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gold rounded-full"></div>
                  <span className="text-gray-300">{t('practice.fastLoans')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gold rounded-full"></div>
                  <span className="text-gray-300">{t('practice.monopolyDefense')}</span>
                </div>
              </div>
            </a>

            <a href={language === 'bg' ? '/veshtno-pravo-nedvizhimi-imoti' : '/property-law-real-estate'} className="group lg:col-span-2 bg-gradient-to-br from-accent to-navy rounded-2xl p-8 text-white transition hover:-translate-y-1 hover:shadow-xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/60">
              <div className="grid lg:grid-cols-[minmax(0,1fr)_auto] gap-8 lg:items-center">
                <div>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10.5L12 3l9 7.5M5 9.5V21h14V9.5M9 21v-7h6v7" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-serif text-white mb-1">{t('practice.propertyLawTitle')}</h3>
                      <p className="text-gold font-medium text-sm">{t('practice.propertyLaw')}</p>
                    </div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">{t('practice.propertyLawDesc')}</p>
                </div>
                <div className="space-y-3 lg:min-w-72">
                  {[t('practice.ownershipDisputes'), t('practice.adversePossession'), t('practice.titleDeeds'), t('practice.propertyFraud')].map(item => (
                    <div key={item} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-gold rounded-full" />
                      <span className="text-gray-300">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </a>
            
            {/* Administrative Law */}
            <a href={language === 'bg' ? '/administrativno-pravo' : '/administrative-law'} className="group flex flex-col bg-gradient-to-br from-navy to-navy/90 rounded-2xl p-8 text-white transition hover:-translate-y-1 hover:shadow-xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/60">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl">⚖️</span>
                </div>
                <div>
                  <h3 className="text-2xl font-serif text-white mb-1">{t('practice.administrativeTitle')}</h3>
                  <p className="text-gold font-medium text-sm">{t('practice.administrative')}</p>
                </div>
              </div>
              
              <p className="text-gray-300 mb-6 leading-relaxed">
                {t('practice.administrativeDesc')}
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gold rounded-full"></div>
                  <span className="text-gray-300">{t('practice.stateInstitutions')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gold rounded-full"></div>
                  <span className="text-gray-300">{t('practice.publicAccess')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gold rounded-full"></div>
                  <span className="text-gray-300">{t('practice.municipalities')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gold rounded-full"></div>
                  <span className="text-gray-300">{t('practice.utilities')}</span>
                </div>
              </div>
            </a>
            
            {/* Pro Bono & Social Advocacy */}
            <div className="bg-gradient-to-br from-gold/90 to-gold/70 rounded-2xl p-8 text-navy">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-navy/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🤝</span>
                </div>
                <div>
                  <h3 className="text-2xl font-serif text-navy mb-1">{t('practice.proBonoTitle')}</h3>
                  <p className="text-navy/80 font-medium text-sm">{t('practice.proBono')}</p>
                </div>
              </div>
              
              <p className="text-navy/80 mb-6 leading-relaxed">
                {t('practice.proBonoDesc')}
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-navy rounded-full"></div>
                  <span className="text-navy/80">{t('practice.singleParents')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-navy rounded-full"></div>
                  <span className="text-navy/80">{t('practice.povertyFamilies')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-navy rounded-full"></div>
                  <span className="text-navy/80">{t('practice.rodAssociation')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-navy rounded-full"></div>
                  <span className="text-navy/80">{t('practice.charityCampaigns')}</span>
                </div>
              </div>
              
            </div>
          </div>
        </div>
      </section>

      {/* Notable Cases Section */}
      <section id="notable-cases" className="py-12 lg:py-24 bg-gradient-to-b from-white to-gray-50 scroll-animate">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-16">
            <div className="inline-flex items-center space-x-2 bg-gold/10 rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-gold rounded-full"></span>
                <span className="text-gold font-medium text-sm">{t('notableCases.badge')}</span>
              </div>
            <h2 className="section-title text-center">{t('notableCases.title')}</h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              {t('notableCases.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Case 1: Daniela Koleva */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden card-hover h-full flex flex-col">
              <div className="p-8 flex flex-col h-full">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gold font-semibold text-sm">{t('notableCases.case1.category')}</span>
                </div>
                <h3 className="text-xl font-serif text-navy mb-3">{t('notableCases.case1.title')}</h3>
                <div className="mb-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gold">{t('notableCases.problemLabel')}</p>
                  <p className="text-slate leading-relaxed">{t('notableCases.case1.problem')}</p>
                </div>
                <div className="mb-6 flex-grow">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gold">{t('notableCases.defenseLabel')}</p>
                  <p className="text-slate leading-relaxed">{t('notableCases.case1.description')}</p>
                </div>
                <div className="border-t border-gray-100 pt-4 flex items-center justify-between mt-auto">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate">{t('notableCases.resultLabel')}</p>
                    <p className="text-sm text-navy font-medium">{t('notableCases.case1.outcome')}</p>
                  </div>
                </div>
                <a href="https://www.24chasa.bg/bulgaria/article/6834047" target="_blank" rel="noopener noreferrer" aria-label={`${t('notableCases.sourceLabel')}: ${language === 'bg' ? '24 часа' : '24 Chasa'}`} className="mt-4 inline-flex rounded font-semibold text-gold underline decoration-gold/40 underline-offset-4 transition-colors hover:text-navy focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/50">{t('notableCases.sourceLabel')}</a>
              </div>
            </div>
            
            {/* Case 2: Gold Concessions */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden card-hover h-full flex flex-col">
              <div className="p-8 flex flex-col h-full">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gold font-semibold text-sm">{t('notableCases.case2.category')}</span>
                </div>
                <h3 className="text-xl font-serif text-navy mb-3">{t('notableCases.case2.title')}</h3>
                <div className="mb-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gold">{t('notableCases.problemLabel')}</p>
                  <p className="text-slate leading-relaxed">{t('notableCases.case2.problem')}</p>
                </div>
                <div className="mb-6 flex-grow">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gold">{t('notableCases.defenseLabel')}</p>
                  <p className="text-slate leading-relaxed">{t('notableCases.case2.description')}</p>
                </div>
                <div className="border-t border-gray-100 pt-4 flex items-center justify-between mt-auto">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate">{t('notableCases.resultLabel')}</p>
                    <p className="text-sm text-navy font-medium">{t('notableCases.case2.outcome')}</p>
                  </div>
                </div>
                <a href="https://www.dunavmost.com/novini/advokatat-koito-mozhe-da-osadi-vseki" target="_blank" rel="noopener noreferrer" aria-label={`${t('notableCases.sourceLabel')}: Dunavmost`} className="mt-4 inline-flex rounded font-semibold text-gold underline decoration-gold/40 underline-offset-4 transition-colors hover:text-navy focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/50">{t('notableCases.sourceLabel')}</a>
              </div>
            </div>
            
            {/* Case 3: Banks & Monopolies */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden card-hover h-full flex flex-col">
              <div className="p-8 flex flex-col h-full">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gold font-semibold text-sm">{t('notableCases.case3.category')}</span>
                </div>
                <h3 className="text-xl font-serif text-navy mb-3">{t('notableCases.case3.title')}</h3>
                <div className="mb-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gold">{t('notableCases.problemLabel')}</p>
                  <p className="text-slate leading-relaxed">{t('notableCases.case3.problem')}</p>
                </div>
                <div className="mb-6 flex-grow">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gold">{t('notableCases.defenseLabel')}</p>
                  <p className="text-slate leading-relaxed">{t('notableCases.case3.description')}</p>
                </div>
                <div className="border-t border-gray-100 pt-4 flex items-center justify-between mt-auto">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate">{t('notableCases.resultLabel')}</p>
                    <p className="text-sm text-navy font-medium">{t('notableCases.case3.outcome')}</p>
                  </div>
                </div>
                <a href="https://www.dunavmost.com/novini/advokatat-koito-mozhe-da-osadi-vseki" target="_blank" rel="noopener noreferrer" aria-label={`${t('notableCases.sourceLabel')}: Dunavmost`} className="mt-4 inline-flex rounded font-semibold text-gold underline decoration-gold/40 underline-offset-4 transition-colors hover:text-navy focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/50">{t('notableCases.sourceLabel')}</a>
              </div>
            </div>
            
            {/* Case 4: Children of Balsa */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden card-hover h-full flex flex-col">
              <div className="p-8 flex flex-col h-full">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gold font-semibold text-sm">{t('notableCases.case4.category')}</span>
                </div>
                <h3 className="text-xl font-serif text-navy mb-3">{t('notableCases.case4.title')}</h3>
                <div className="mb-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gold">{t('notableCases.problemLabel')}</p>
                  <p className="text-slate leading-relaxed">{t('notableCases.case4.problem')}</p>
                </div>
                <div className="mb-6 flex-grow">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gold">{t('notableCases.defenseLabel')}</p>
                  <p className="text-slate leading-relaxed">{t('notableCases.case4.description')}</p>
                </div>
                <div className="border-t border-gray-100 pt-4 flex items-center justify-between mt-auto">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate">{t('notableCases.resultLabel')}</p>
                    <p className="text-sm text-navy font-medium">{t('notableCases.case4.outcome')}</p>
                  </div>
                </div>
                <a href="https://darik.bg/advokat-nikolov-vinovni-za-panikata-v-romskite-mahali-sa-instituciite-koito-otkazvat-dia" target="_blank" rel="noopener noreferrer" aria-label={`${t('notableCases.sourceLabel')}: ${language === 'bg' ? 'Дарик' : 'Darik'}`} className="mt-4 inline-flex rounded font-semibold text-gold underline decoration-gold/40 underline-offset-4 transition-colors hover:text-navy focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/50">{t('notableCases.sourceLabel')}</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-12 lg:py-24 bg-white scroll-animate">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-16">
            <div className="inline-flex items-center space-x-2 bg-gold/10 rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-gold rounded-full"></span>
                <span className="text-gold font-medium text-sm">{t('faq.badge')}</span>
              </div>
            <h2 className="section-title text-center">{t('faq.title')}</h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              {t('faq.subtitle')}
            </p>
          </div>
          
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <details key={i} className="group bg-white rounded-2xl shadow-lg overflow-hidden">
                <summary className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-serif font-semibold text-navy pr-4">{t(`faq.q${i}`)}</h3>
                  <span className="text-gold flex-shrink-0 transition-transform group-open:rotate-45 text-2xl font-light">+</span>
                </summary>
                <div className="px-6 pb-6 text-slate leading-relaxed">
                  {t(`faq.a${i}`)}
                </div>
              </details>
            ))}
          </div>
        </div>

        <HeadJsonLd id="home-faq-jsonld" data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [1, 2, 3, 4, 5].map((i) => ({
            "@type": "Question",
            "name": t(`faq.q${i}`),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": t(`faq.a${i}`)
            }
          }))
        }} />
      </section>

      {/* Blog Section */}
      <section id="blog" className="py-12 lg:py-24 bg-gradient-to-b from-white to-gray-50 scroll-animate">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-16">
            <div className="inline-flex items-center space-x-2 bg-gold/10 rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-gold rounded-full"></span>
                <span className="text-gold font-medium text-sm">{t('blog.badge')}</span>
              </div>
            <h2 className="section-title text-center">{t('blog.title')}</h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              {t('blog.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {loading ? (
              <div className="col-span-3 text-center text-slate">{t('blog.loading')}</div>
            ) : articles.filter(article => article.language === language).length === 0 ? (
              <div className="col-span-3 text-center text-slate">{t('blog.notFound')}</div>
            ) : (
              articles
                .filter(article => article.language === language)
                .slice(0, 3)
                .map((article) => (
                  <article key={article.id} className="group bg-white rounded-2xl shadow-lg overflow-hidden card-hover h-full flex flex-col">
                    <div className="p-6 flex flex-col flex-grow">
                      <div className="flex items-center space-x-2 mb-4">
                        <span className="text-gold text-sm font-medium">{article.date}</span>
                      </div>
                      <a href={articlePath(article.slug)} className="text-xl font-serif text-navy mb-3 group-hover:text-gold transition-colors block hover:text-gold">
                        {article.title}
                      </a>
                      <p className="text-slate mb-4 leading-relaxed flex-grow">
                        {article.description}
                      </p>
                      <a href={articlePath(article.slug)} className="text-gold font-semibold hover:text-gold/80 transition-colors mt-auto">
                        {t('blog.readArticle')}
                      </a>
                    </div>
                  </article>
                ))
            )}
          </div>
          
          <div className="text-center mt-12">
            <a href="/blog" className="btn-primary">
              {t('blog.viewAllArticles')}
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 lg:py-24 bg-navy text-white scroll-animate">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-16">
            <div className="inline-flex items-center space-x-2 bg-gold/10 rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-gold rounded-full"></span>
                <span className="text-gold font-medium text-sm">{t('contact.badge')}</span>
              </div>
            <h2 className="section-title text-center text-white">{t('contact.title')}</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-serif text-navy mb-6">{t('contact.formTitle')}</h3>
              <form ref={formRef} onSubmit={sendEmail} className="space-y-6">
                {/* Honeypot field - hidden from humans, traps bots */}
                <div className="hidden" aria-hidden="true">
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" />
                </div>
                {/* Timestamp field for EmailJS {{time}} variable - will be set on submit */}
                <div className="hidden" aria-hidden="true">
                  <input type="text" name="time" readOnly />
                </div>
                <div>
                  <label htmlFor="contact-name" className="block text-navy font-semibold mb-2">{t('contact.name')}</label>
                  <input id="contact-name" type="text" name="name" required className="w-full px-4 py-3 border border-border-gray rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all duration-300 text-navy" placeholder={t('contact.namePlaceholder')} />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-navy font-semibold mb-2">{t('contact.emailLabel')}</label>
                  <input id="contact-email" type="email" name="email" required className="w-full px-4 py-3 border border-border-gray rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all duration-300 text-navy" placeholder={t('contact.emailPlaceholder')} />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="block text-navy font-semibold mb-2">{t('contact.phoneLabel')}</label>
                  <input id="contact-phone" type="tel" name="title" className="w-full px-4 py-3 border border-border-gray rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all duration-300 text-navy" placeholder={t('contact.phonePlaceholder')} />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-navy font-semibold mb-2">{t('contact.message')}</label>
                  <textarea
                    id="contact-message" 
                    rows={4} 
                    name="message" 
                    required 
                    maxLength={1000} 
                    onChange={(e) => setMessageLength(e.target.value.length)}
                    className="w-full px-4 py-3 border border-border-gray rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all duration-300 resize-none text-navy" 
                    placeholder={t('contact.messagePlaceholder')}
                  ></textarea>
                  <p className="text-sm text-gray-500 text-right mt-1">
                    {messageLength}/1000 {language === 'bg' ? 'знака' : 'characters'}
                  </p>
                </div>
                {sendStatus && (
                  <p className={`text-center ${sendStatusType === 'error' ? 'text-red-600' : 'text-green-600'}`}>
                    {sendStatus}
                  </p>
                )}
                <button type="submit" disabled={isSending} className="btn-primary w-full text-lg disabled:opacity-50">
                  {isSending ? (language === 'bg' ? 'Изпращане...' : 'Sending...') : t('contact.submitRequest')}
                </button>
              </form>
            </div>
            
            <div className="space-y-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                <h3 className="text-2xl font-serif text-white mb-6">{t('contact.contactInfo')}</h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-gold font-semibold mb-2">{t('contact.varnaOffice')}</h4>
                    <p className="text-gray-300">{t('contact.varnaAddress')}</p>
                  </div>
                  <div>
                    <h4 className="text-gold font-semibold mb-2">{t('contact.sofiaOffice')}</h4>
                    <p className="text-gray-300">{t('contact.sofiaAddress')}</p>
                  </div>
                  <div>
                    <h4 className="text-gold font-semibold mb-2">{t('contact.phoneLabel')}</h4>
                    <a href="tel:+359882744951" className="text-gray-300 hover:text-gold transition-colors">{t('contact.phone')}</a>
                  </div>
                  <div>
                    <h4 className="text-gold font-semibold mb-2">{t('contact.emailLabel')}</h4>
                    <a href="mailto:kantora@advokatnikolov.com" className="text-gray-300 hover:text-gold transition-colors">{t('contact.email')}</a>
                  </div>
                  <div>
                    <h4 className="text-gold font-semibold mb-2">{t('contact.officeHours')}</h4>
                    <p className="text-gray-300">{t('contact.hours')}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                <h3 className="text-2xl font-serif text-white mb-4">{t('contact.varnaOffice')}</h3>
                <div className="rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2909.5!2d27.9193!3d43.2110!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x40a455ebaa1391b5%3A0x1dbe72fd1fe7ea55!2zQWR2b2thdHNrYSBrYW50b3JhIFBldGFyIE5pa29sb3Y!5e0!3m2!1sen!2sbg!4v1"
                    title="Google Maps - Varna Office Location"
                    width="100%"
                    height="250"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Floating Mobile CTA */}
      <div className="fixed bottom-6 right-6 z-50 lg:hidden flex flex-col gap-3">
        <a
          href="tel:+359882744951"
          className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 transition-colors"
          aria-label={language === 'bg' ? 'Обадете се' : 'Call us'}
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        </a>
        <button
          onClick={() => {
            const contactEl = document.getElementById('contact');
            if (contactEl) contactEl.scrollIntoView({ behavior: 'smooth' });
          }}
          className="w-14 h-14 bg-gold rounded-full flex items-center justify-center shadow-lg hover:bg-yellow-500 transition-colors"
          aria-label={language === 'bg' ? 'Консултация' : 'Consultation'}
        >
          <svg className="w-6 h-6 text-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
      </div>

      {/* Back to Top Button */}
      <BackToTopButton />
    </div>
  );
}

export default OnePagerHome;